import { useState } from 'react'
import { Link } from 'react-router-dom'
import { LuPlus, LuSettings2, LuScrollText, LuChartPie, LuCreditCard, LuKeyRound } from 'react-icons/lu'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import ButtonBase from '@mui/material/ButtonBase'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { getUserId } from '../lib/workos'
import useAuth from '../models/useAuth'
import useOrganizations, { orgLabel } from '../models/useOrganizations'
import useProfile from '../models/useProfile'
import AvatarForm from './AvatarForm'
import DeadlineEmailSettings from './DeadlineEmailSettings'
import NotificationSettings from './NotificationSettings'
import OwnerAvatar from './OwnerAvatar'
import ModalOrg from './ModalOrg'
import { useAuthState } from '../hooks/useAuthState'
import { useOrgRole } from '../hooks/useOrgRole'

export default function Profile() {
  const { fullName, website, avatarUrl, avatarImage, ...profile } = useProfile()
  const { session } = useAuth()
  const { signOut } = useAuthState()
  const { organizations, activeOrgId, setActiveOrg } = useOrganizations()
  const { can } = useOrgRole()
  const [creating, setCreating] = useState(false)
  const myUserId = getUserId()
  const activeOrg = organizations.find(o => o.id === activeOrgId)

  // Nothing requires a name — every consumer falls back. But an unnamed personal org
  // falls back to its owner's email (router 0091), so quote the label the user is
  // actually wearing rather than nagging in the abstract. orgLabel, not .name: the
  // name is NULL in exactly the case this fires on. Once the owner has named the org
  // themselves that explicit name wins, and a profile name relabels nothing — so
  // don't promise it does. Personal-org id == profile id since 1.1.
  const myOrg = organizations.find(o => o.personal && o.id === myUserId)
  const nameNudge = !myOrg
    ? 'Add your name — it labels your organization and is how teammates see you in shared networks.'
    : myOrg.name
      ? 'Add your name — it is how teammates see you in shared networks.'
      : `Add your name — your organization is labeled “${orgLabel(myOrg)}” until you do.`

  if (!session) return null

  return (
    <Stack direction="column" spacing={3} sx={{
      maxWidth: 400
    }}>
      {/* Organizations — above the account form */}
      <Box>
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1
          }}>
          <Typography variant="subtitle2" sx={{
            color: 'text.secondary'
          }}>
            Organizations
          </Typography>
          <Stack direction="row">
            <Tooltip title="Usage" placement="top" arrow>
              <IconButton size="small" component={Link} to="/usage" color="inherit">
                <LuChartPie size={18} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Billing" placement="top" arrow>
              <IconButton size="small" component={Link} to="/billing" color="inherit">
                <LuCreditCard size={18} />
              </IconButton>
            </Tooltip>
            {can('members.manage') && (
              <Tooltip title="Audit log" placement="top" arrow>
                <IconButton size="small" component={Link} to="/account/audit" color="inherit">
                  <LuScrollText size={18} />
                </IconButton>
              </Tooltip>
            )}
            {/* SSO is Owner-only and meaningless for a personal (one-person) org. */}
            {can('sso.manage') && !activeOrg?.personal && (
              <Tooltip title="Single sign-on" placement="top" arrow>
                <IconButton size="small" component={Link} to="/org/sso" color="inherit">
                  <LuKeyRound size={18} />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Manage" placement="top" arrow>
              <IconButton size="small" component={Link} to="/org/members" color="inherit">
                <LuSettings2 size={18} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Create organization" placement="top" arrow>
              <IconButton
                size="small"
                // + toggles the dialog. Its press or tap must not reach the ClickAwayListener, or the
                // listener closes the dialog and the click opens a new, empty one.
                onMouseDown={event => creating && event.stopPropagation()}
                onTouchEnd={event => creating && event.stopPropagation()}
                onClick={() => setCreating(open => !open)}
                color="inherit"
              >
                <LuPlus size={18} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        <Stack direction="row" spacing={2} sx={{ overflowX: 'auto', pt: 1, pb: 0.5 }}>
          {organizations.map(org => {
            const selected = org.id === activeOrgId
            const isMine = org.id === myUserId
            return (
              <ButtonBase
                key={org.id}
                onClick={() => setActiveOrg(org.id)}
                focusRipple
                aria-pressed={selected}
                sx={{ flexShrink: 0, width: 84, borderRadius: 2, p: 0.5 }}
              >
                <Stack
                  spacing={0.5}
                  sx={{
                    alignItems: 'center',
                    width: '100%'
                  }}>
                  <OwnerAvatar
                    url={isMine ? (avatarImage ?? org.ownerAvatarUrl) : org.ownerAvatarUrl}
                    sx={{
                      width: 56,
                      height: 56,
                      border: '2px solid',
                      borderColor: selected ? 'text.primary' : 'transparent',
                    }}
                  />
                  <Typography
                    variant="caption"
                    align="center"
                    sx={{
                      lineHeight: 1.2,
                      fontWeight: selected ? 600 : 400,
                      color: selected ? 'text.primary' : 'text.secondary',
                    }}
                  >
                    {orgLabel(org)}
                  </Typography>
                </Stack>
              </ButtonBase>
            )
          })}
        </Stack>
        {creating && (
          // The inline dialog has no backdrop, so a click outside it is caught here.
          // ⚠ onMouseDown, not the default onClick: a drag that selects text in the name field and
          // ends outside the dialog sends its click to a common ancestor, outside the dialog. With
          // onClick that closed the dialog and lost the typed name.
          <ClickAwayListener mouseEvent="onMouseDown" onClickAway={() => setCreating(false)}>
            <Box>
              <ModalOrg inline onClose={() => setCreating(false)} />
            </Box>
          </ClickAwayListener>
        )}
      </Box>
      <Divider />
      {/* Account */}
      <Box>
        <Typography
          variant="subtitle2"
          sx={{
            color: 'text.secondary',
            mb: 2
          }}>
          Account
        </Typography>
        <Stack spacing={2}>
          <TextField label="Email" value={session.user.email} disabled />
          <TextField
            label="Name"
            id="Full Name"
            type="text"
            helperText={fullName ? undefined : nameNudge}
            value={fullName || ''}
            onChange={e => profile.set({ fullName: e.target.value })}
            onBlur={() => profile.save({ fullName })}
            onSubmit={() => profile.save({ fullName })}
          />
          <TextField
            label="Website"
            id="website"
            type="url"
            value={website || ''}
            onChange={e => profile.set({ website: e.target.value })}
            onBlur={() => profile.save({ website })}
            onSubmit={() => profile.save({ website })}
          />
          <AvatarForm
            url={avatarUrl}
            size={150}
            onUpload={path => profile.save({ avatarUrl: path })}
            onRemove={() => profile.save({ avatarUrl: null })}
          />
        </Stack>
      </Box>
      {/* The app only (mobile/): a browser receives no push and draws nothing here. */}
      <NotificationSettings />
      <DeadlineEmailSettings />
      <Divider />
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
        <Button type="button" onClick={async () => await signOut()} variant="contained" color="primary">
          Sign Out
        </Button>
      </Stack>
    </Stack>
  )
}
